import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, type ApiResponse, useAxiosInstance } from 'shared/hooks/utils';

import { addActionNameToMutationResult } from 'shared/hooks/utils/add-action-name-to-mutation-result';
import { z } from 'zod/v4';

const updateProductVariablesSchema = z.object({
  id: z.string(),
  description: z.string().optional().nullable(),
  name: z.string().optional(),
  priceHtva: z.coerce.number().optional(),
  taxCategoryCode: z.string().optional(),
  archived: z.boolean().optional(),
});

export type UpdateProductVariables = z.infer<typeof updateProductVariablesSchema>;

export async function updateProductFn(
  axiosInstance: Promise<AxiosInstance>,
  data: UpdateProductVariables,
) {
  const instance = await axiosInstance;

  return instance.put(`products/${data.id}`, updateProductVariablesSchema.parse(data));
}

export function useUpdateProduct() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateProductVariables>({
    mutationFn: variables => updateProductFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['products'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateProductVariables,
    'updateProduct'
  >('updateProduct', mutation);
}
